import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { Icon } from "../../design/components";
import { METHODS } from "../../design/methods";
import { HEALTH_PILL, METHOD_BY_CONNECTION, STATUS_LABEL } from "../../domain/health";
import type { EntitySummary } from "../../api/types";
import styles from "./CompaniesList.module.css";

export type CompanyFilters = {
  q: string;
  status: string;
  health: string;
  method: string;
  wave: string;
};

const QUERY_DEBOUNCE_MS = 250;

const STATUS_OPTIONS: EntitySummary["status"][] = ["live", "onboarding", "not_started"];
const HEALTH_OPTIONS: EntitySummary["health"][] = ["healthy", "failing", "stopped_reporting", "onboarding"];
const METHOD_OPTIONS: EntitySummary["connectionMethod"][] = ["direct_api", "agent", "file"];

export function CompaniesFilters({
  filters,
  waves,
  onChange,
  onClear,
}: {
  filters: CompanyFilters;
  waves: string[];
  onChange: (patch: Partial<CompanyFilters>) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [localQ, setLocalQ] = useState(filters.q);

  useEffect(() => setLocalQ(filters.q), [filters.q]);

  useEffect(() => {
    if (localQ === filters.q) return;
    const timer = window.setTimeout(() => onChange({ q: localQ }), QUERY_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQ]);

  const hasFilters = Boolean(filters.q || filters.status || filters.health || filters.method || filters.wave);

  return (
    <div className={styles.filters}>
      <div className="search">
        <Icon name="search" />
        <input
          className="input input-sm"
          placeholder={t("companies.filters.searchPlaceholder", "Search company, CR or participant ID")}
          value={localQ}
          onChange={(event) => setLocalQ(event.target.value)}
          aria-label={t("companies.filters.searchAriaLabel", "Search companies")}
        />
      </div>
      <select
        className="select select-sm"
        aria-label={t("companies.filters.statusAriaLabel", "Filter by status")}
        value={filters.status}
        onChange={(event) => onChange({ status: event.target.value })}
      >
        <option value="">{t("companies.filters.allStatuses", "All statuses")}</option>
        {STATUS_OPTIONS.map((status) => (
          <option key={status} value={status}>
            {STATUS_LABEL[status]}
          </option>
        ))}
      </select>
      <select
        className="select select-sm"
        aria-label={t("companies.filters.healthAriaLabel", "Filter by health")}
        value={filters.health}
        onChange={(event) => onChange({ health: event.target.value })}
      >
        <option value="">{t("companies.filters.allHealth", "All health")}</option>
        {HEALTH_OPTIONS.map((health) => (
          <option key={health} value={health}>
            {HEALTH_PILL[health].label}
          </option>
        ))}
      </select>
      <select
        className="select select-sm"
        aria-label={t("companies.filters.methodAriaLabel", "Filter by method")}
        value={filters.method}
        onChange={(event) => onChange({ method: event.target.value })}
      >
        <option value="">{t("companies.filters.allMethods", "All methods")}</option>
        {METHOD_OPTIONS.map((method) => (
          <option key={method} value={method}>
            {METHODS[METHOD_BY_CONNECTION[method]].full}
          </option>
        ))}
      </select>
      <select
        className="select select-sm"
        aria-label={t("companies.filters.waveAriaLabel", "Filter by wave")}
        value={filters.wave}
        onChange={(event) => onChange({ wave: event.target.value })}
      >
        <option value="">{t("companies.filters.allWaves", "All waves")}</option>
        {waves.map((wave) => (
          <option key={wave} value={wave}>
            {wave}
          </option>
        ))}
      </select>
      {hasFilters ? (
        <button type="button" className="btn btn-sm btn-ghost" onClick={onClear}>
          {t("companies.filters.clearButton", "Clear filters")}
        </button>
      ) : null}
    </div>
  );
}
